// src/features/onboarding/hooks/useTipAutoMarker.ts
// 自動標記 Tip 為已讀的 Hook
// 當配置了 autoMarkOnEvent 的 Tip 對應事件發生時，自動調用 markTipAsSeen

import { useEffect } from 'react';
import { eventBus } from '../../../utils/eventBus';
import { logInfo } from '../../../utils/logger';
import { useOnboarding } from '../contexts/OnboardingContext';
import { tips } from '../constants/tipDefinitions';

/**
 * 建立事件名稱到 Tip ID 列表的映射
 * 支援多對一：多個 Tip 可監聽同一事件
 * 支援一對多：一個 Tip 可監聽多個事件（陣列）
 */
function buildEventToTipsMap(): Map<string, string[]> {
  const map = new Map<string, string[]>();

  Object.values(tips).forEach(tip => {
    if (!tip.autoMarkOnEvent) return;

    // 支援 string 或 string[] 兩種格式
    const events = Array.isArray(tip.autoMarkOnEvent) ? tip.autoMarkOnEvent : [tip.autoMarkOnEvent];

    events.forEach(eventName => {
      const existing = map.get(eventName) ?? [];
      existing.push(tip.id);
      map.set(eventName, existing);
    });
  });

  return map;
}

/**
 * 自動標記 Tip Hook
 *
 * 掃描所有 Tip 定義中配置了 autoMarkOnEvent 的項目，
 * 訂閱對應事件，事件發生時自動標記 Tip 為已讀。
 *
 * 在需要啟用自動標記的頁面調用此 Hook 即可。
 */
export function useTipAutoMarker(): void {
  const { markTipAsSeen, shouldShowTip } = useOnboarding();

  useEffect(() => {
    const eventToTipsMap = buildEventToTipsMap();

    // 無配置時直接返回，零開銷
    if (eventToTipsMap.size === 0) return;

    const unsubscribers: (() => void)[] = [];

    Array.from(eventToTipsMap.entries()).forEach(([eventName, tipIds]) => {
      const unsubscribe = eventBus.subscribe(eventName, () => {
        tipIds.forEach(tipId => {
          if (shouldShowTip(tipId)) {
            logInfo('TipAutoMarker', `Auto-marking tip "${tipId}" on event "${eventName}"`);
            markTipAsSeen(tipId);
          }
        });
      });
      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [markTipAsSeen, shouldShowTip]);
}
